// Subtasks list and inline add UI block for TaskForm.
import { useState } from 'react';
import { Box, Fade } from '@mui/material';
import { AddTaskRow } from '@/components/ui/AddTaskRow';
import TaskInputInline from '@/features/tasks/components/forms/TaskInputInline';
import FieldLabel from '@/features/tasks/components/forms/FieldLabel';
import { renderFlatSubtaskRows } from '@/features/tasks/components/forms/TaskFormSubtaskRows';
import TaskGroupSubtasks from '@/features/tasks/components/lists/TaskGroupSubtasks';
import { useT } from '@/i18n/useT';

/**
 * @param {object} props
 * @param {boolean} props.isNewTask
 * @param {string} [props.parentId]
 * @param {import('@/types').Task[]} props.subtaskRows
 * @param {Function} props.onToggleComplete
 * @param {Function} props.onAddPendingSubtask
 * @param {Function} props.onSaveSubtask
 */
export function TaskFormSubtasksBlock({
  isNewTask,
  parentId,
  subtaskRows,
  onToggleComplete,
  onAddPendingSubtask,
  onSaveSubtask,
}) {
  const t = useT();
  const [isAddingPendingSubtask, setIsAddingPendingSubtask] = useState(false);
  const [isAddingExistingSubtask, setIsAddingExistingSubtask] = useState(false);
  const isAdding = isAddingPendingSubtask || isAddingExistingSubtask;

  // Existing task: interactive list with disableRowNavigate (no modal-on-modal).
  if (!isNewTask && parentId) {
    return (
      <TaskGroupSubtasks
        parentId={parentId}
        subtasks={subtaskRows}
        onSaveSubtask={onSaveSubtask}
        disableRowNavigate
        closeOnSave
        emptyStateMessage={t(
          'taskform.noSubtasks',
          'No subtasks yet.',
        )}
      />
    );
  }

  // New task: pending local rows (no TaskItem navigate surface yet).
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <FieldLabel>{t('taskform.subtasks', 'Sub-tasks')}</FieldLabel>
      {subtaskRows.length > 0
        ? renderFlatSubtaskRows(subtaskRows, onToggleComplete)
        : null}
      <Fade key={isAdding ? 'input' : 'add-row'} in timeout={150}>
        <Box>
          {!isAdding ? (
            <AddTaskRow
              onClick={() =>
                isNewTask
                  ? setIsAddingPendingSubtask(true)
                  : setIsAddingExistingSubtask(true)
              }
            />
          ) : (
            <TaskInputInline
              closeOnSave={true}
              onCancel={() => {
                setIsAddingPendingSubtask(false);
                setIsAddingExistingSubtask(false);
              }}
              onSave={async (parsedData) => {
                if (isNewTask) await onAddPendingSubtask(parsedData);
                else await onSaveSubtask(parsedData);
                setIsAddingPendingSubtask(false);
                setIsAddingExistingSubtask(false);
              }}
            />
          )}
        </Box>
      </Fade>
    </Box>
  );
}
